import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, LIMIT_FIELDS, type Tenant, type TenantLimits } from "../api";

// Platform-admin only: create tenants, change status, edit per-tenant limits, delete.
// All calls are platform-scoped, so they must not carry the x-sf-tenant header.
const PLATFORM = { noTenant: true } as const;

export function TenantsPage() {
  const qc = useQueryClient();
  const [form, setForm] = useState({ slug: "", name: "" });
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);

  const tenants = useQuery({
    queryKey: ["tenants", "platform"],
    queryFn: () => api<{ tenants: Tenant[] }>("/v1/tenants", PLATFORM).then((r) => r.tenants),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["tenants", "platform"] });

  const create = useMutation({
    mutationFn: () => api<Tenant>("/v1/tenants", { method: "POST", body: form, ...PLATFORM }),
    onSuccess: () => { setForm({ slug: "", name: "" }); setError(null); invalidate(); },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  const setStatus = useMutation({
    mutationFn: (v: { id: string; status: "active" | "suspended" }) =>
      api(`/v1/tenants/${v.id}`, { method: "PATCH", body: { status: v.status }, ...PLATFORM }),
    onSuccess: invalidate,
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/v1/tenants/${id}`, { method: "DELETE", ...PLATFORM }),
    onSuccess: invalidate,
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  const list = tenants.data ?? [];
  const active = list.filter((t) => t.status === "active").length;
  const suspended = list.filter((t) => t.status === "suspended").length;

  return (
    <section aria-labelledby="tenants-title">
      <div className="page-head">
        <span className="eyebrow">Platform administration</span>
        <h1 id="tenants-title">Tenants</h1>
        <p className="section-sub">Create and manage the tenants that share this SignalFabric deployment.</p>
      </div>

      <div className="stats">
        <div className="stat"><div className="num">{list.length}</div><div className="lbl">Total</div></div>
        <div className="stat"><div className="num ok">{active}</div><div className="lbl">Active</div></div>
        <div className="stat"><div className="num warn">{suspended}</div><div className="lbl">Suspended</div></div>
      </div>

      <form className="card" onSubmit={(e: FormEvent) => { e.preventDefault(); create.mutate(); }}>
        <h2>New tenant</h2>
        <div className="row">
          <label className="grow">
            Slug
            <input
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              placeholder="acme"
              pattern="[a-z0-9][a-z0-9-]{1,62}"
              title="lowercase letters, digits and hyphens; 2–63 chars"
              required
            />
          </label>
          <label className="grow">
            Name <span className="muted">(optional)</span>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Acme Corp" />
          </label>
          <button type="submit" className="amber" disabled={!form.slug || create.isPending}>Create tenant</button>
        </div>
        {error && <p role="alert" className="error">{error}</p>}
      </form>

      <table className="grid">
        <caption className="sr-only">Tenants</caption>
        <thead>
          <tr>
            <th scope="col">Slug</th><th scope="col">Name</th><th scope="col">Status</th>
            <th scope="col"><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {list.length === 0 && (
            <tr><td colSpan={4}><div className="empty"><span className="eyebrow">No tenants yet</span>Create your first tenant with the form above.</div></td></tr>
          )}
          {list.map((t) => (
            <TenantRow
              key={t.id}
              tenant={t}
              open={editing === t.id}
              onToggleLimits={() => setEditing(editing === t.id ? null : t.id)}
              onSuspend={() => setStatus.mutate({ id: t.id, status: "suspended" })}
              onActivate={() => setStatus.mutate({ id: t.id, status: "active" })}
              onDelete={() =>
                window.confirm(`Delete tenant "${t.slug}"? This removes all of its data and cannot be undone.`) &&
                remove.mutate(t.id)}
            />
          ))}
        </tbody>
      </table>
    </section>
  );
}

function TenantRow(props: {
  tenant: Tenant; open: boolean; onToggleLimits: () => void;
  onSuspend: () => void; onActivate: () => void; onDelete: () => void;
}) {
  const { tenant: t } = props;
  return (
    <>
      <tr>
        <td><code>{t.slug}</code></td>
        <td>{t.name}</td>
        <td><span className={`badge st-${t.status}`}>{t.status}</span></td>
        <td className="row" style={{ gap: ".4rem" }}>
          <button type="button" className="link" onClick={props.onToggleLimits} aria-expanded={props.open}>
            {props.open ? "Hide limits" : "Limits"}
          </button>
          {t.status === "active"
            ? <button type="button" className="ghost small" onClick={props.onSuspend}>Suspend</button>
            : <button type="button" className="ghost small" onClick={props.onActivate}>Activate</button>}
          <button type="button" className="danger small" onClick={props.onDelete}>Delete</button>
        </td>
      </tr>
      {props.open && (
        <tr>
          <td colSpan={4}><LimitsEditor tenant={t} /></td>
        </tr>
      )}
    </>
  );
}

function LimitsEditor({ tenant }: { tenant: Tenant }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Partial<Record<keyof TenantLimits, string>>>({});
  const [error, setError] = useState<string | null>(null);

  const limits = useQuery({
    queryKey: ["tenant-limits", tenant.id],
    queryFn: () => api<TenantLimits>(`/v1/tenants/${tenant.id}/limits`, PLATFORM),
  });

  const save = useMutation({
    mutationFn: () => {
      const body: Partial<TenantLimits> = {};
      for (const [k, v] of Object.entries(draft)) {
        if (v !== undefined && v !== "") body[k as keyof TenantLimits] = Number(v);
      }
      return api<TenantLimits>(`/v1/tenants/${tenant.id}/limits`, { method: "PUT", body, ...PLATFORM });
    },
    onSuccess: (fresh) => {
      setDraft({}); setError(null);
      qc.setQueryData(["tenant-limits", tenant.id], fresh);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  if (limits.isLoading) return <p className="muted small">Loading limits…</p>;
  if (limits.isError) return <p role="alert" className="error small">Could not load limits.</p>;

  return (
    <form
      className="card"
      aria-label={`Limits for ${tenant.slug}`}
      onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(); }}
    >
      <div className="row">
        {LIMIT_FIELDS.map(({ key, label }) => (
          <label key={key} className="grow">
            {label}
            <input
              type="number"
              min={0}
              value={draft[key] ?? String(limits.data?.[key] ?? "")}
              onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
            />
          </label>
        ))}
      </div>
      <div className="row">
        <button type="submit" className="amber small" disabled={save.isPending || Object.keys(draft).length === 0}>Save limits</button>
        {Object.keys(draft).length > 0 && (
          <button type="button" className="link" onClick={() => setDraft({})}>Reset</button>
        )}
      </div>
      {error && <p role="alert" className="error small">{error}</p>}
    </form>
  );
}
